import { isNil } from 'lodash';
import { assertNotNil } from '../../../utils/types';
import { ITooltipProps } from '../hooks/tooltip/useSingleChartDataTooltip';
import {
  IParsedChartDataExpression,
  IParsedChartDataRecord,
} from '../interfaces';
import { TimeUnit } from '../../../utils/apollo/graphqlSchema';
import styled from 'styled-components';
import {
  defaultNumberFormatOptions,
  globalFormat,
} from '../../../utils/formatting';
import PercentChangeLabel from '../../PercentChangeLabel';

interface IProps {
  data: IParsedChartDataRecord[];
  linesMeta: IParsedChartDataExpression['linesMeta'];
  tooltipProps: ITooltipProps;
  timeUnit: string;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TextHint = styled.div`
  font-size: 10px;
  flex: 1;
`;

const getPrevTextByTimeUnit = (timeUnit: string) => {
  switch (timeUnit) {
    case TimeUnit.Day:
      return 'previous day';
    case TimeUnit.Week:
      return 'previous week';
    case TimeUnit.Month:
      return 'previous month';
    case TimeUnit.Quarter:
      return 'previous quarter';
    case TimeUnit.Year:
      return 'previous year';
    case TimeUnit.FourWeek:
      return 'previous 4 weeks';
    default:
      return 'previous period';
  }
};

// The percentChangeValue can be: 1, > 1, or < 1. That's why we need these
// calculations to get a percentage change
const getStatusAndFinalPercentChangeValue = (
  percentChangeValue: number,
): number => {
  if (percentChangeValue > 1) {
    return percentChangeValue - 1;
  } else if (percentChangeValue < 1) {
    return 1 - percentChangeValue;
  } else {
    return 0;
  }
};

const TooltipExpressionDataChangeValue = ({
  data,
  linesMeta,
  tooltipProps,
  timeUnit,
}: IProps) => {
  const lineMeta = linesMeta[tooltipProps.line.name];
  // if the line is not found, return null, it could happen when the line is OTHER
  if (isNil(lineMeta)) {
    return null;
  }

  const dayData = data?.find((d) => {
    return d[tooltipProps.xAxisKey] === tooltipProps.date;
  });
  assertNotNil(dayData);
  const percentChangeKey = lineMeta.percentChangeLineKey;
  // if lineMeta doesn't have percentChangeLineKey, or the value is not found, return null
  // for linemeta part, ma, cumulative lines are not supported
  // for data, the first data point doesn't have percentChangeValue compare to previous data point
  if (
    !percentChangeKey ||
    !dayData[percentChangeKey] ||
    typeof dayData[percentChangeKey] !== 'number'
  ) {
    return null;
  }
  const percentChangeValue = dayData[percentChangeKey] as number;
  const changedValue = getStatusAndFinalPercentChangeValue(percentChangeValue);
  return (
    <Container>
      <PercentChangeLabel percentageChange={percentChangeValue} compareTo={1}>
        {globalFormat.formatNumber(
          changedValue,
          defaultNumberFormatOptions.percent,
        )}
      </PercentChangeLabel>
      <TextHint>change from {getPrevTextByTimeUnit(timeUnit)}</TextHint>
    </Container>
  );
};

export default TooltipExpressionDataChangeValue;
